/**
 * 튜토리얼 엔진 조립 팩토리 — 어댑터 3종 + overlay 옵션(uiTokens 주입)을 한 곳에 모은다.
 *
 * GameScene(god-object)이 어댑터 생성·overlay 옵션을 직접 들지 않도록 분리. overlay의 uiTokens 의존을
 * `tutorial/` 경계 안에 격리한다. store는 getter 주입이라 applySaveData의 SaveData 참조 교체에도 항상 최신을 본다.
 */
import type Phaser from "phaser";
import {
  DEFAULT_MESSAGE_AUTO_ADVANCE_MS,
  TutorialRunner,
  type TutorialTranslate,
  type TutorialAnalyticsSink,
} from "@yjsgames/phaser-tutorial";
import { CONTENT_PANEL_STYLE, FONT_FAMILY } from "../ui/uiTokens.js";
import { PALETTE_NUM } from "../themes/palette.js";
import { JsonTutorialScenarioSource } from "./JsonTutorialScenarioSource.js";
import { GameTargetResolver } from "./GameTargetResolver.js";
import { getActiveInsets, getActiveLayout } from "../ui/sceneLayout.js";
import { SaveDataTutorialStore } from "./SaveDataTutorialStore.js";
import { getTextDirection } from "../i18n/i18n.js";

export interface TutorialRunnerDeps {
  /** 완료 시나리오 id 목록 getter (SaveData 위임 — in-place 갱신 반영). */
  readonly getCompleted: () => string[];
  /** 재개 스텝 인덱스 맵 getter. */
  readonly getResume: () => Record<string, number>;
  /** 진행/완료 변경 시 저장 콜백. */
  readonly persist: () => void;
  /** 로컬라이즈 함수 (앱 i18n t). */
  readonly translate: TutorialTranslate;
  /** 분석 sink (선택) — 앱 AnalyticsSink와 동일 시그니처. */
  readonly analytics?: TutorialAnalyticsSink;
}

/** 튜토리얼 엔진 조립 — source(generated JSON) + resolver(LAYOUT 좌표) + store(SaveData 위임) + overlay 옵션. */
export function createTutorialRunner(
  scene: Phaser.Scene,
  deps: TutorialRunnerDeps,
): TutorialRunner {
  return new TutorialRunner({
    scene,
    source: new JsonTutorialScenarioSource(),
    resolver: new GameTargetResolver(scene.scale.width, scene.scale.height, getActiveLayout(scene)),
    store: new SaveDataTutorialStore(deps.getCompleted, deps.getResume, deps.persist),
    overlay: {
      screenWidth: scene.scale.width,
      screenHeight: scene.scale.height,
      panelStyle: CONTENT_PANEL_STYLE,
      fontFamily: FONT_FAMILY,
      translate: deps.translate,
      // 메시지 본문 `{icon:start}`/`{icon:fire}` → 그리드뷰 Start/Fire 아이콘과 동일 텍스처·tint(SSoT=ModuleGridView).
      // 튜토리얼 "Start/Fire" 글자 옆에 실제 게임 아이콘을 병기해 화면과 시각 매칭.
      inlineIcons: {
        start: { textureKey: "icon_grid_start", size: 16, tint: PALETTE_NUM.endpointStart },
        fire: { textureKey: "icon_grid_fire", size: 16, tint: PALETTE_NUM.endpointFire },
      },
      // 손가락 드래그/탭 연출 텍스처(흰 실루엣, TutorialPointer가 렌더). place 스텝 = 팔레트→그리드 드래그.
      pointerTextureKey: "tutorial_hand",
      textDirection: getTextDirection,
      safeAreaInsets: getActiveInsets(scene),
      // depth(1500)/dim 색은 엔진 기본값 사용 — 게임 모달(DEPTH.toastModal 2000)보다 아래, HUD/탭바보다 위.
    },
    defaultMessageAutoAdvanceMs: DEFAULT_MESSAGE_AUTO_ADVANCE_MS,
    analytics: deps.analytics,
  });
}
